type Func = (...x: unknown[]) => unknown;
type Arguments<F> = F extends (...x: infer A) => unknown ? A : never;
type Promised<T> = T extends Promise<infer U> ? Promise<U> : Promise<T>;

/**
 * Debounce function execution by interval. Only latest call is executed.
 *
 * Example:
 * const debouncedAction = debounce(action, 200);
 * ...
 * [1, 2, 3, 4].forEach(() => debouncedAction([args])); // Only latest gets executed
 *
 * @param func Function to enhance/wrap
 * @param interval How long to wait for consecutive calls before triggering func
 */
export const debounce = <TFunc extends Func>(
  func: TFunc,
  interval: number
): ((...args: Arguments<TFunc>) => Promised<ReturnType<TFunc>>) => {
  let timer: number;
  return (...args: Arguments<TFunc>): Promised<ReturnType<TFunc>> => {
    clearTimeout(timer);
    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      timer = setTimeout(() => resolve(func(...args)), interval);
    }) as Promised<ReturnType<TFunc>>;
  };
};

/**
 * Debounce callback function execution by interval. Only latest call is executed.
 *
 * Example:
 * const debouncedAction = debounceCallback(action, 200);
 * ...
 * [1, 2, 3, 4].forEach(() => debouncedAction([args])); // Only latest gets executed
 *
 * @param func Function to enhance/wrap
 * @param interval How long to wait for consecutive calls before triggering func
 * @return func() => timeoutId
 */
export const debounceCallback = <TFunc extends Func>(
  func: TFunc,
  interval: number
): ((...args: Arguments<TFunc>) => ReturnType<typeof setTimeout>) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Arguments<TFunc>): ReturnType<typeof setTimeout> => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), interval);
    return timeoutId;
  };
};
